import { useEffect } from "react";
import {
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "@/shared/hooks/use-toast";
import { receivableSnapshotApi } from "../api";
import type {
  GetReceivableSnapshotSummaryParams,
  PaginatedReceivableSnapshotSummaryResponse,
  ReceivableSnapshotCreator,
} from "../utils/types";
import { receivableSnapshotKeys } from "../utils/receivable-snapshots.keys";

export function useReceivableSnapshotSummaries(
  filters?: GetReceivableSnapshotSummaryParams,
  options?: Omit<UseQueryOptions<PaginatedReceivableSnapshotSummaryResponse, Error>, "queryKey" | "queryFn">,
) {
  const query = useQuery({
    queryKey: receivableSnapshotKeys.summary(filters),
    queryFn: ({ signal }) => receivableSnapshotApi.listSummaries(filters, signal),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<PaginatedReceivableSnapshotSummaryResponse, Error>, "queryKey" | "queryFn">),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar cálculos",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error]);

  return {
    summaries: query.data?.data ?? [],
    totalPages: query.data?.totalPage ?? 0,
    totalItems: query.data?.totalItems ?? 0,
    currentPage: query.data?.page ?? filters?.page ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useReceivableSnapshotDetail(
  paidDate?: string,
  options?: Omit<UseQueryOptions<ReceivableSnapshotCreator[], Error>, "queryKey" | "queryFn">,
) {
  const query = useQuery({
    queryKey: paidDate ? receivableSnapshotKeys.detail(paidDate) : [...receivableSnapshotKeys.details(), ''],
    queryFn: ({ signal }) => {
      if (!paidDate) throw new Error("Data de pagamento não informada");
      return receivableSnapshotApi.getDetail(paidDate, signal).then(data => data);
    },
    enabled: Boolean(paidDate),
    staleTime: 60_000,
    ...(options as Omit<UseQueryOptions<ReceivableSnapshotCreator[], Error>, "queryKey" | "queryFn">),
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar detalhes",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error]);

  return {
    snapshots: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    queryResult: query,
  };
}
